import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { PowerRound, PowerBurstState } from '../../../interfaces/power-burst.interface';

/**
 * Host shell for Power Burst. Reads `PowerBurstState`, shows `base^exp` with its
 * repeated-multiplication expansion, and lets the stage handle the answer orbs.
 * Outcomes are written with `scorePowerBurstRound` + `advancePowerBurstRound`.
 */
@Component({
  selector: 'app-power-burst-game',
  templateUrl: './power-burst-game.component.html',
  styleUrls: ['./power-burst-game.component.scss']
})
export class PowerBurstGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  resolving = false;
  lastXp = 0;

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.showSuccess = false;
          this.resolving = false;
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): PowerBurstState | null {
    return (this.session?.gameState as PowerBurstState) ?? null;
  }
  get round(): PowerRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  /** e.g. "2 × 2 × 2 × 2 × 2" for 2^5. */
  get expansion(): string {
    const r = this.round;
    if (!r) return '';
    return Array(r.exp).fill(r.base).join(' × ');
  }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Outcomes (from the stage) ──────────────────────────────────────

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scorePowerBurstRound(true);
    this.showSuccess = true;
    this.later(() => this.miniGameService.advancePowerBurstRound(), 3200);
  }

  onMissed(): void {
    if (this.resolving) return;
    this.gameAudioService.play('assets/game-mode/sound-effects/buzzerSoundEffect.mp3');
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scorePowerBurstRound(false);
    this.later(() => this.miniGameService.advancePowerBurstRound(), 250);
  }
}
